import { cn } from '@/lib/utils';
import { type CSSProperties, type ReactNode, createContext, useContext } from 'react';

/**
 * The scope selector the *shell* puts in a project page's band: which project
 * the page below belongs to. It is provided by the layout rather than passed by
 * each page because it is shell state, not page state — a page that forgot it
 * would be a page that silently interrogates the wrong project (spec §24).
 *
 * Admin and public shells provide nothing, so their bands have no project
 * selector at all.
 */
const PageBandScope = createContext<ReactNode>(null);

export function PageBandScopeProvider({
  scope,
  children,
}: {
  scope?: ReactNode;
  children: ReactNode;
}) {
  return <PageBandScope.Provider value={scope ?? null}>{children}</PageBandScope.Provider>;
}

export function usePageBandScope(): ReactNode {
  return useContext(PageBandScope);
}

export function PageHeader({
  title,
  subtitle,
  pill,
  facts,
  breadcrumb,
  extra,
  titleClamp,
  titleAttr,
  style,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  pill?: ReactNode;
  /**
   * The band's own facts, on their own row under the title: a `FactGrid` of the
   * two or three things the page is *about* (spec §13.6). They get the full
   * band width rather than the left column the title and actions share, so a
   * code and a module name never break mid-value on a 1024px screen.
   */
  facts?: ReactNode;
  /** Optional breadcrumb row rendered above the title */
  breadcrumb?: ReactNode;
  extra?: ReactNode;
  /** Clamp long titles to this many lines instead of wrapping. */
  titleClamp?: number;
  /** Native tooltip for a clamped title. */
  titleAttr?: string;
  /**
   * Appended to the band's own padding. A drawer owes the sheet's floating
   * close button a right inset: the band is the only thing at its height, and a
   * title given the whole width runs under the X (spec §52).
   */
  style?: CSSProperties;
}) {
  const scope = usePageBandScope();
  return (
    <div
      data-slot="page-header"
      // The band is its own container. Whether the title and the page actions
      // can share a row is a question about *this band's* width, and the
      // viewport is the wrong ruler for it: the same band is ~1136px on a page
      // and ~868px inside a drawer, so a `md:` rule kept them side by side at
      // 900px and left the title two lines of a three-line clamp (spec §52).
      className="@container flex flex-col gap-3"
      style={{
        padding: 'var(--space-6) var(--page-gutter) var(--space-4)',
        maxWidth: 'var(--page-max)',
        boxSizing: 'border-box',
        width: '100%',
        ...style,
      }}
    >
      {/* Title and actions bottom-align, so the primary button sits on the
          subtitle's baseline rather than floating beside the title. */}
      <div className="flex flex-col gap-3 @min-[960px]:flex-row @min-[960px]:items-end @min-[960px]:justify-between">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {breadcrumb && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-muted)',
                marginBottom: 'var(--space-2)',
              }}
            >
              {breadcrumb}
            </div>
          )}
          <div className="flex items-center" style={{ gap: 'var(--space-3)' }}>
            <h1
              // The clamp is a class rather than four inline properties: on a
              // phone the title is the only thing in the band, so it gets more
              // lines than the two a desktop band shares with its actions
              // (`.page-title-clamp` in globals.css).
              className={cn('m-0', titleClamp && 'page-title-clamp')}
              title={titleAttr}
              style={{
                fontSize: 'var(--fs-2xl)',
                fontWeight: 700,
                color: 'var(--text)',
                letterSpacing: '-0.025em',
                lineHeight: 'var(--lh-tight)',
                ...(titleClamp ? ({ '--title-clamp': titleClamp } as CSSProperties) : {}),
              }}
            >
              {title}
            </h1>
            {pill}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 'var(--fs-md)',
                color: 'var(--text-muted)',
                marginTop: 'var(--space-1)',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {/* Scope first, page action second: the reader confirms which project
            they are looking at before pressing the button that writes to it. */}
        {(scope || extra) && (
          <div
            className="flex flex-wrap items-center @min-[960px]:justify-end"
            style={{ gap: 'var(--space-2)' }}
          >
            {scope}
            {extra}
          </div>
        )}
      </div>
      {facts && <div>{facts}</div>}
    </div>
  );
}
